import React, { useState, useEffect, useMemo } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import { Card, Table, Button, Space, Select, DatePicker, Spin } from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import {
  REPORT_DATE_FORMAT,
  parseStoredDateRange,
  serializeDateRange,
  formatReportDate,
} from "../utils/reportDateRangeUtils";
import {
  DEFAULT_SUFFIX_OPTIONS,
  DEFAULT_SELECTED_SUFFIXES,
  selectAllDropdownRender,
} from "../utils/suffixFilterUtils";

const { RangePicker } = DatePicker;
const { Option } = Select;

const FILTERS_STORAGE_KEY = "report:po-so-sales-bill:filters";

// Filters saved by the last visit (Back navigation / refresh). Read once, before the
// first render, so the very first request already uses them.
const readStoredFilters = () => {
  try {
    const raw = window.sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const PoSoSalesBillReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [stored] = useState(readStoredFilters);
  const [suffixFilter, setSuffixFilter] = useState(() => {
    const restored = Array.isArray(stored.suffixFilter)
      ? stored.suffixFilter.filter(Boolean)
      : [];
    return restored.length > 0 ? restored : DEFAULT_SELECTED_SUFFIXES;
  });
  const [partyFilter, setPartyFilter] = useState(
    () => (typeof stored.partyFilter === "string" && stored.partyFilter) || null,
  );
  const [brandFilter, setBrandFilter] = useState(
    () => (typeof stored.brandFilter === "string" && stored.brandFilter) || null,
  );
  const [vendorFilter, setVendorFilter] = useState(
    () => (typeof stored.vendorFilter === "string" && stored.vendorFilter) || null,
  );
  const [partyOptions, setPartyOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [dateRange, setDateRange] = useState(() =>
    parseStoredDateRange(stored.dateRange),
  );
  const [financialYear, setFinancialYear] = useState(
    () =>
      (typeof stored.financialYear === "string" && stored.financialYear) ||
      getCurrentFinancialYear(),
  );
  const [downloading, setDownloading] = useState(false);
  const [grandTotals, setGrandTotals] = useState(null);

  // Persist filters whenever they change.
  useEffect(() => {
    try {
      const payload = {
        financialYear,
        suffixFilter,
        partyFilter: partyFilter || "",
        brandFilter: brandFilter || "",
        vendorFilter: vendorFilter || "",
        dateRange: serializeDateRange(dateRange),
      };
      window.sessionStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify(payload),
      );
    } catch (e) {
      // Ignore quota / storage issues
    }
  }, [financialYear, suffixFilter, partyFilter, brandFilter, vendorFilter, dateRange]);

  useEffect(() => {
    const fetchPartiesAndBrands = async () => {
      try {
        const res = await fetch(
          `${apiBaseUrl}/inventoryManagement/getSOPartiesAndBrands`,
          {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          },
        );
        const j = await res.json();
        if (!j.success) return;
        setPartyOptions(
          (j.parties || []).map((name) => ({
            label: String(name).trim(),
            value: String(name).trim(),
          })),
        );
        setBrandOptions(
          (j.brands || []).map((name) => ({
            label: String(name).trim(),
            value: String(name).trim(),
          })),
        );
      } catch (e) {
        console.error("Error fetching parties/brands:", e);
      }
    };
    fetchPartiesAndBrands();
  }, [apiBaseUrl]);

  useEffect(() => {
    const fetchVendors = async () => {
      const token = window.localStorage.getItem("token");
      try {
        const response = await fetch(
          `${apiBaseUrl}/inventoryManagement/getFilterDataForPO`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              authorization: `Token ${token}`,
            },
          },
        );
        const result = await response.json();
        if (result.success && result.data && result.data.vendors) {
          setVendorOptions(
            (result.data.vendors || []).map((v) => {
              const name = typeof v === "string" ? v : (v?.name ?? "");
              return { label: name, value: name };
            }),
          );
        }
      } catch (error) {
        console.error("Failed to fetch vendor options:", error);
      }
    };
    fetchVendors();
  }, [apiBaseUrl]);

  const hasNarrowingFilters = useMemo(
    () =>
      Boolean(
        (suffixFilter && suffixFilter.length > 0) ||
          partyFilter ||
          brandFilter ||
          vendorFilter ||
          (dateRange && dateRange[0]),
      ),
    [suffixFilter, partyFilter, brandFilter, vendorFilter, dateRange],
  );

  const reportQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("financialYear", financialYear);
    if (suffixFilter && suffixFilter.length > 0) {
      suffixFilter.forEach((suffix) => params.append("suffix", suffix));
    }
    if (partyFilter) params.append("partyName", partyFilter);
    if (brandFilter) params.append("brandName", brandFilter);
    if (vendorFilter) params.append("vendorName", vendorFilter);
    const startDate = formatReportDate(dateRange?.[0]);
    const endDate = formatReportDate(dateRange?.[1]);
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    return params.toString();
  }, [financialYear, suffixFilter, partyFilter, brandFilter, vendorFilter, dateRange]);

  const beginRequest = useLatestRequest();

  // Fetch report only when at least one narrowing filter is applied (FY is always sent when fetching)
  useEffect(() => {
    if (!hasNarrowingFilters) {
      beginRequest(); // drop any answer still on its way
      setLoading(false);
      return;
    }

    const isCurrent = beginRequest();

    const fetchReport = async () => {
      setLoading(true);
      setGrandTotals(null);
      try {
        const response = await fetch(
          `${apiBaseUrl}/inventoryManagement/getPoSoSalesBillReport?${reportQueryParams}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const result = await response.json();
        if (!isCurrent()) return;

        if (result.success) {
          setData(result.data || []);
          setGrandTotals(result.grandTotals ?? null);
        } else {
          console.error("Failed to fetch report:", result.message);
          setGrandTotals(null);
        }
      } catch (error) {
        if (!isCurrent()) return;
        console.error("Error fetching PO SO Sales Bill report:", error);
        setGrandTotals(null);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };

    fetchReport();
  }, [apiBaseUrl, reportQueryParams, hasNarrowingFilters]);

  const handleDownloadExcel = async () => {
    if (!hasNarrowingFilters) return;
    setDownloading(true);
    try {
      const excelQuery = `?${reportQueryParams}&isExcel=true`;
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getPoSoSalesBillReport${excelQuery}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `PO_SO_Sales_Bill_Report_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error("Failed to download Excel file");
      }
    } catch (error) {
      console.error("Error downloading Excel file:", error);
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === "" || amount == null) return "₹0.00";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const columns = [
    {
      title: "PO No.",
      dataIndex: "poNo",
      key: "poNo",
      width: 120,
      align: "center",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px", fontWeight: "bold" }}>
          {v || "-"}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 100,
      align: "center",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
      ),
    },
    {
      title: "Party Name",
      dataIndex: "partyName",
      key: "partyName",
      width: 200,
      align: "left",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
      ),
    },
    {
      title: "Brand Name",
      dataIndex: "brandName",
      key: "brandName",
      width: 140,
      align: "left",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
      ),
    },
    {
      title: "Agency / Sales Party",
      dataIndex: "agencySalesParty",
      key: "agencySalesParty",
      width: 160,
      align: "left",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
      ),
    },
    {
      title: "Ref No.",
      dataIndex: "refNo",
      key: "refNo",
      width: 90,
      align: "center",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
      ),
    },
    {
      title: "PO Amount",
      dataIndex: "poAmount",
      key: "poAmount",
      width: 110,
      align: "right",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {v != null && v !== "" && Number(v) !== 0
            ? formatCurrency(v)
            : "-"}
        </span>
      ),
    },
    {
      title: "SO No.",
      dataIndex: "soNo",
      key: "soNo",
      width: 110,
      align: "center",
      render: (v, record) => {
        const soId = record?.soId;
        if (!v || !soId) {
          return (
            <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
          );
        }

        return (
          <span
            role="link"
            tabIndex={0}
            style={{
              color: "#1677ff",
              fontSize: "12px",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/sales-order/${soId}?tab=1`)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(`/sales-order/${soId}?tab=1`);
              }
            }}
            title="Open Sales Order"
          >
            {v}
          </span>
        );
      },
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      width: 100,
      align: "center",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
      ),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      width: 100,
      align: "center",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
      ),
    },
    {
      title: "SO Amount",
      dataIndex: "soAmount",
      key: "soAmount",
      width: 110,
      align: "right",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {v != null ? formatCurrency(v) : "₹0.00"}
        </span>
      ),
    },
    {
      title: "Bill No.",
      dataIndex: "billNo",
      key: "billNo",
      width: 90,
      align: "center",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {v !== "" && v != null ? v : "-"}
        </span>
      ),
    },
    {
      title: "Bill Amount",
      dataIndex: "billAmount",
      key: "billAmount",
      width: 110,
      align: "right",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {v !== "" && v != null ? formatCurrency(v) : "-"}
        </span>
      ),
    },
    {
      title: "Salesman",
      dataIndex: "salesman",
      key: "salesman",
      width: 120,
      align: "left",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
      ),
    },
    {
      title: "Suffix",
      dataIndex: "suffix",
      key: "suffix",
      width: 80,
      align: "center",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>{v || "-"}</span>
      ),
    },
    {
      title: "No. of Hoarding",
      dataIndex: "noOfHoarding",
      key: "noOfHoarding",
      width: 110,
      align: "center",
      render: (v) => (
        <span style={{ color: "#333", fontSize: "12px" }}>
          {v != null ? v : "-"}
        </span>
      ),
    },
  ];

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
            fontSize: "12px",
            fontWeight: "bold",
            padding: "8px 4px",
          }}
        />
      ),
    },
  };

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
        minHeight: "100vh",
      }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              PO → SO → Sales → Bill Report
            </Space>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                padding: "12px 0",
                marginBottom: "4px",
              }}
            >
              <Space wrap size="middle">
                <Select
                  value={financialYear}
                  onChange={setFinancialYear}
                  style={{ width: 120 }}
                  placeholder="Select FY"
                >
                  {getFinancialYearOptions().map((fy) => (
                    <Option key={fy} value={fy}>
                      {fy}
                    </Option>
                  ))}
                </Select>
                <Select
                  mode="multiple"
                  placeholder="Filter by Suffix"
                  options={DEFAULT_SUFFIX_OPTIONS}
                  value={suffixFilter.length ? suffixFilter : undefined}
                  onChange={(v) => setSuffixFilter(v ?? [])}
                  style={{ minWidth: 140 }}
                  allowClear
                  dropdownRender={selectAllDropdownRender(
                    DEFAULT_SUFFIX_OPTIONS,
                    suffixFilter,
                    setSuffixFilter,
                  )}
                />
                <Select
                  placeholder="Filter by Party"
                  options={partyOptions}
                  value={partyFilter}
                  onChange={setPartyFilter}
                  style={{ width: 230 }}
                  allowClear
                  showSearch
                filterOption={(input, opt) => {
                  const label = String(opt?.label ?? "").toLowerCase();
                  const query = String(input ?? "").toLowerCase();
                  if (!query) return true;
                  return label.startsWith(query);
                }}
                />
                <Select
                  placeholder="Filter by Brand"
                  options={brandOptions}
                  value={brandFilter}
                  onChange={setBrandFilter}
                  style={{ width: 210 }}
                  allowClear
                  showSearch
                  filterOption={(input, opt) => {
                    const label = String(opt?.label ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                />
              </Space>
              <Space wrap size="middle">
                <Select
                  placeholder="Filter by Vendor"
                  options={vendorOptions}
                  value={vendorFilter}
                  onChange={setVendorFilter}
                  style={{ width: 210 }}
                  allowClear
                  showSearch
                filterOption={(input, opt) => {
                  const label = String(opt?.label ?? "").toLowerCase();
                  const query = String(input ?? "").toLowerCase();
                  if (!query) return true;
                  return label.startsWith(query);
                }}
                />
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format={REPORT_DATE_FORMAT}
                  style={{ width: 220 }}
                  allowClear
                />
                <Button
                  type="primary"
                  icon={<FaDownload />}
                  loading={downloading}
                  disabled={!hasNarrowingFilters}
                  onClick={handleDownloadExcel}
                >
                  Download Excel
                </Button>
              </Space>
            </div>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : !hasNarrowingFilters ? (
          <div
            style={{
              textAlign: "center",
              padding: "80px 24px",
              color: "#8c8c8c",
              fontSize: 15,
              lineHeight: 1.6,
              maxWidth: 420,
              margin: "0 auto",
            }}
          >
            <p style={{ margin: 0, fontWeight: 500, color: "#595959" }}>
              Apply filters to view the report
            </p>
            <p style={{ margin: "8px 0 0", fontSize: 13 }}>
              Use the filters above (Suffix, Party, Brand, Vendor, or Date range).
            </p>
          </div>
        ) : !data?.length && !grandTotals ? (
          <div style={{ textAlign: "center", padding: 60, color: "#8c8c8c", fontSize: 14 }}>
            No records found for the selected filters.
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={data || []}
            loading={false}
            components={components}
            rowKey={(record, index) =>
              (record.poNo || "") + (record.soNo || "") + (record.suffix || "") + index
            }
            pagination={false}
            scroll={{ x: 1800, y: 600 }}
            size="small"
            summary={() =>
              grandTotals ? (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={6} align="center">
                      <strong>Grand Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      <strong>
                        {formatCurrency(grandTotals.poAmount ?? 0)}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="center" />
                    <Table.Summary.Cell index={8} align="center" />
                    <Table.Summary.Cell index={9} align="center" />
                    <Table.Summary.Cell index={10} align="right">
                      <strong>
                        {formatCurrency(grandTotals.soAmount ?? 0)}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={11} align="center" />
                    <Table.Summary.Cell index={12} align="right">
                      <strong>
                        {formatCurrency(grandTotals.billAmount ?? 0)}
                      </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={13} align="left" />
                    <Table.Summary.Cell index={14} align="center" />
                    <Table.Summary.Cell index={15} align="center" />
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        )}
      </Card>
    </div>
  );
};

export default PoSoSalesBillReport;
